/**
 * Holds the on-screen wallet balance while a round's outcome is still being
 * animated.
 *
 * The wallet books an originals round's stake and payout together, and the SSE
 * stream pushes the resulting balance a few milliseconds after the click — well
 * before the scene shows the outcome. A balance that drops (a loss) or jumps (a
 * win) at click time tells the player the result before the card turns. While
 * any hold is active, balance writes are parked by key; the newest write per key
 * lands when the last hold is released.
 */

// A scene that never reports its reveal (a crash, a tab frozen mid-animation)
// must not leave the header showing a stale balance for the rest of the visit.
export const MAX_HOLD_MS = 20_000;

let activeHolds = 0;
const parked = new Map<string, () => void>();

export function holdWalletBalance(maxHoldMs: number = MAX_HOLD_MS): () => void {
  activeHolds += 1;
  let released = false;
  const release = (): void => {
    if (released) return;
    released = true;
    clearTimeout(timer);
    activeHolds -= 1;
    if (activeHolds === 0) flushParked();
  };
  const timer = setTimeout(release, maxHoldMs);
  return release;
}

export function whenWalletBalanceUnheld(key: string, apply: () => void): void {
  if (activeHolds === 0) {
    apply();
    return;
  }
  parked.set(key, apply);
}

function flushParked(): void {
  const pending = [...parked.values()];
  parked.clear();
  for (const apply of pending) apply();
}
